import { LayoutEditorShell } from '../../../lib/form-layout/LayoutEditorShell';
import {
  CONTRACT_LAND_FORM_FIELDS,
  CONTRACT_LAND_FORM_FIELD_NAMES,
  CONTRACT_LAND_DEFAULT_LAYOUT,
  type ContractLandFormContext,
} from '../../../lib/form-layout/contract-land-fields';
import {
  useAdminActiveContractLandLayout,
  useUpdateContractLandFormLayout,
} from '../../../queries/contract-land-form-layout';
import { useLandLookups } from '../../../queries/land-lookups';
import type { ContractLandInputSchema } from '../../../lib/schemas';

/**
 * Thin wrapper around LayoutEditorShell that wires the contract-form's
 * registry / queries / preview context. Mechanical clone of
 * LeaseFormLayoutEditor.tsx.
 *
 * The contract registry's lookup-backed selects (contract_type,
 * flag_for_issues) read their options off `context.lookups` — same pattern
 * as the entry editor threading `docTypes`/`prospects` through
 * `previewContext`. The query hook is called here, once, and never inside
 * the renderer's per-field loop (which would break the Rules of Hooks).
 */
export function ContractLandFormLayoutEditor() {
  const { data: lookups } = useLandLookups();

  const previewDefaults: ContractLandInputSchema = {
    contract_number: '',
    contract_title: null,
    contract_type: null,
    prospect: null,
    field: null,
    reviewer_user_id: null,
    party_1: null,
    other_parties: null,
    county: null,
    state: null,
    township: null,
    range: null,
    section: null,
    block: null,
    contract_date: null,
    book: null,
    page: null,
    entry: null,
    flag_for_issues: null,
    issue_remark: null,
    description: null,
    depth_limitations: false,
    depth_remarks: null,
    royalty: false,
    orri: false,
    non_consent: false,
    non_consent_penalty: null,
    call_on_production: false,
    special_pooling_provisions: false,
    special_pooling_remarks: null,
    gas_balancing: false,
    joa_form_type: null,
    change_of_operator: null,
    remarks: null,
    reviewer_notes: null,
    change_remarks: null,
  };

  const previewContext: ContractLandFormContext = {
    lookups: lookups ?? {
      lease_type: [],
      lease_status: [],
      lease_ownership: [],
      fed_rights: [],
      contract_type: [],
      flag_for_issues: [],
      well_status: [],
      well_type: [],
      unit_type: [],
    },
    // The layout editor's preview form has no real project/session behind
    // it, so the reviewer Select has no members to offer — canAssignAnyReviewer:
    // true just keeps the (empty) option list unfiltered rather than
    // collapsing to a self-only view that can never match anything here.
    members: [],
    canAssignAnyReviewer: true,
    currentUserId: 'preview',
  };

  return (
    <LayoutEditorShell
      title="Contract form layout"
      breadcrumbLabel="Contract form layout"
      registry={CONTRACT_LAND_FORM_FIELDS}
      allNames={CONTRACT_LAND_FORM_FIELD_NAMES}
      defaultLayout={CONTRACT_LAND_DEFAULT_LAYOUT}
      useActive={useAdminActiveContractLandLayout}
      useUpdate={useUpdateContractLandFormLayout}
      previewDefaults={previewDefaults}
      previewContext={previewContext}
    />
  );
}
